import { ENVIRONMENT_INITIALIZER, EnvironmentProviders, inject, Provider } from '@angular/core';
import { provideFirebaseApp } from '@angular/fire/app';
import { provideFirestore } from '@angular/fire/firestore';

import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';

import { environment } from '../../../environments/environment';
import { FirebaseAuthSyncService } from '../services/auth/firebase-auth-sync.service';
import { FIREBASE_APP, FIREBASE_AUTH, FIRESTORE_TOKEN } from '../tokens/firebase.tokens';

/**
 * Returns the Firebase provider set for `app.config.ts`.
 *
 * Registers both the raw SDK injection tokens (`FIREBASE_APP`, `FIREBASE_AUTH`,
 * `FIRESTORE_TOKEN`) and the AngularFire providers (`provideFirebaseApp`,
 * `provideFirestore`) from the **same** `FirebaseApp` instance — no duplicate
 * initialization. Existing services that `inject(Firestore)` from
 * `@angular/fire/firestore` continue to work unchanged.
 *
 * When `environment.firebase` is absent all raw tokens resolve to `null` and
 * the AngularFire providers are omitted, so no Firebase SDK is loaded.
 *
 * The Firebase token endpoint is NOT configured here. `FirebaseAuthSyncService`
 * requests the custom token from Nick Fury at `ClusterService.furyBaseUrl()` at
 * call time, so it tracks the user's selected cluster (Nick Fury is deployed per
 * cluster).
 *
 * @example
 * ```typescript
 * // app.config.ts
 * export const appConfig: ApplicationConfig = {
 *   providers: [...getFirebaseProviders()],
 * };
 * ```
 */
export function getFirebaseProviders(): (Provider | EnvironmentProviders)[] {
  if (!environment.firebase) {
    return [
      { provide: FIREBASE_APP, useValue: null },
      { provide: FIREBASE_AUTH, useValue: null },
      { provide: FIRESTORE_TOKEN, useValue: null },
    ];
  }

  // Single FirebaseApp instance shared across all tokens — Firebase SDK returns
  // the same object for repeated `initializeApp` calls with the same config.
  const app = initializeApp(environment.firebase);
  const auth = getAuth(app);
  const firestore = getFirestore(app);

  return [
    // AngularFire providers — used by existing Firestore services that inject
    // `Firestore` from `@angular/fire/firestore`. We pass the already-initialized
    // app so AngularFire reuses it instead of calling initializeApp again.
    provideFirebaseApp(() => app),
    provideFirestore(() => firestore),

    // Raw SDK injection tokens — used by FirebaseAuthSyncService.
    { provide: FIREBASE_APP, useValue: app },
    { provide: FIREBASE_AUTH, useValue: auth },
    { provide: FIRESTORE_TOKEN, useValue: firestore },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(FirebaseAuthSyncService).start(),
    },
  ];
}
